import { apiClient } from 'axios-client';
import FormField from 'components/form-field/form-field';
import { Formik } from 'formik';
import React, { FunctionComponent, useCallback } from 'react';
import { Button, Form, Icon } from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import * as Yup from 'yup';

type ReplyReviewFormProps = {
  reviewId: number;
  initialReply?: string;
  onCompleted: (reply?: string) => void;
  onRefused?: (reply: string) => void;
  type?: 'reply' | 'reply-moderation';
};

export const ReplyReviewForm: FunctionComponent<ReplyReviewFormProps> = ({
  reviewId,
  onCompleted,
  onRefused,
  type = 'reply',
  initialReply,
}) => {
  const replyReview = useCallback(
    async (reply: string) => {
      if (type === 'reply') {
        await apiClient
          .post(`/form/review-pro/reply`, {
            proReviewId: reviewId,
            reply: reply.trim(),
          })
          .catch((error) => {
            ToastError('Erreur', "Impossible de répondre à l'avis");
            throw error;
          });
        ToastSuccess('Succès', 'Réponse envoyée');
      }
      onCompleted(type === 'reply' ? undefined : reply);
    },
    [reviewId, type, onCompleted],
  );

  return (
    <Formik
      validationSchema={Yup.object().shape({
        reply: Yup.string().required('Réponse requise !'),
      })}
      validateOnChange={true}
      initialValues={{ reply: initialReply ?? '' }}
      onSubmit={async (values, actions): Promise<void> => {
        await replyReview(values.reply);
        actions.setSubmitting(false);
      }}
    >
      {(props): React.ReactElement => (
        <Form onReset={props.handleReset} onSubmit={props.handleSubmit}>
          <FormField
            type="textarea"
            placeholder={type === 'reply' ? 'Réponse' : 'Réponse à modérer'}
            label={type === 'reply' ? 'Réponse' : 'Réponse à modérer'}
            helpText={
              type === 'reply'
                ? 'La réponse est visible sur le site web et est envoyée par email aux deux parties'
                : "La réponse sera envoyée au pro et visible sur le site web après publication de l'avis"
            }
            name="reply"
            rows={type === 'reply-moderation' ? 10 : undefined}
            required
          />
          <Button.Group>
            <Button
              primary
              type="submit"
              loading={props.isSubmitting}
              disabled={props.isSubmitting}
            >
              {type === 'reply' && <Icon name="mail" />}
              {type === 'reply' ? 'Envoyer' : 'Valider'}
            </Button>
            {type === 'reply-moderation' && <Button.Or text="ou" />}
            {type === 'reply-moderation' && (
              <Button
                negative
                type="button"
                onClick={(): void => {
                  if (onRefused) {
                    onRefused(props.values.reply);
                  }
                }}
              >
                Refuser
              </Button>
            )}
          </Button.Group>
        </Form>
      )}
    </Formik>
  );
};
